"use client";

import { useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, DollarSign, Clock, TrendingUp, Shield } from "lucide-react";

export default function RoiCalculatorPage() {
  const [sdrCount, setSdrCount] = useState(2);
  const [toolCost, setToolCost] = useState(500);
  const [monthlyEmails, setMonthlyEmails] = useState(2000);
  const [currentReplyRate, setCurrentReplyRate] = useState(2);
  const [calculated, setCalculated] = useState(false);

  const sdrCostPerMonth = sdrCount * 6000; // ~$72K/year per SDR
  const currentTotalCost = sdrCostPerMonth + toolCost;
  const peakflowCost = sdrCount <= 1 ? 49 : sdrCount <= 5 ? 149 : 399;

  const monthlySavings = currentTotalCost - peakflowCost;
  const annualSavings = monthlySavings * 12;
  const projectedReplyRate = Math.min(currentReplyRate * 3.2, 25);
  const timeSavedHoursPerWeek = sdrCount * 15; // 15 hours saved per SDR per week
  const projectedMeetings = Math.round((monthlyEmails * (projectedReplyRate / 100)) * 0.3);

  return (
    <section className="px-4 py-24 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-16 text-center">
          <span className="mb-4 inline-block rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
            ROI Calculator
          </span>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Calculate Your Peakflow ROI
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            See how much time and money you could save by switching to Peakflow.
          </p>
        </div>

        <div className="grid gap-10 lg:grid-cols-2">
          {/* Inputs */}
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <h2 className="mb-6 text-lg font-semibold">Your Current Setup</h2>
            <div className="space-y-6">
              <div>
                <Label htmlFor="sdr-count">Number of SDRs</Label>
                <Input
                  id="sdr-count"
                  type="number"
                  min={0}
                  max={50}
                  value={sdrCount}
                  onChange={(e) => setSdrCount(Number(e.target.value))}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="tool-cost">Monthly tool costs ($)</Label>
                <Input
                  id="tool-cost"
                  type="number"
                  min={0}
                  value={toolCost}
                  onChange={(e) => setToolCost(Number(e.target.value))}
                  className="mt-1.5"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Total spend on outbound tools (email, LinkedIn, CRM, enrichment, etc.)
                </p>
              </div>
              <div>
                <Label htmlFor="monthly-emails">Monthly outbound messages</Label>
                <Input
                  id="monthly-emails"
                  type="number"
                  min={0}
                  value={monthlyEmails}
                  onChange={(e) => setMonthlyEmails(Number(e.target.value))}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="reply-rate">Current reply rate (%)</Label>
                <Input
                  id="reply-rate"
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={currentReplyRate}
                  onChange={(e) => setCurrentReplyRate(Number(e.target.value))}
                  className="mt-1.5"
                />
              </div>

              <Button
                size="lg"
                className="w-full"
                onClick={() => setCalculated(true)}
              >
                Calculate My ROI
              </Button>
            </div>
          </div>

          {/* Results */}
          <div>
            {calculated ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <h2 className="text-lg font-semibold">Your Projected Results</h2>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-5">
                    <DollarSign className="mb-2 h-6 w-6 text-green-500" />
                    <div className="text-2xl font-bold text-foreground">
                      ${annualSavings.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">Annual savings</div>
                  </div>
                  <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5">
                    <Clock className="mb-2 h-6 w-6 text-blue-500" />
                    <div className="text-2xl font-bold text-foreground">
                      {timeSavedHoursPerWeek}h/week
                    </div>
                    <div className="text-sm text-muted-foreground">Time saved</div>
                  </div>
                  <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-5">
                    <TrendingUp className="mb-2 h-6 w-6 text-purple-500" />
                    <div className="text-2xl font-bold text-foreground">
                      {projectedReplyRate.toFixed(1)}%
                    </div>
                    <div className="text-sm text-muted-foreground">Projected reply rate</div>
                  </div>
                  <div className="rounded-xl border border-orange-500/20 bg-orange-500/5 p-5">
                    <Shield className="mb-2 h-6 w-6 text-orange-500" />
                    <div className="text-2xl font-bold text-foreground">
                      {projectedMeetings}
                    </div>
                    <div className="text-sm text-muted-foreground">Projected meetings/mo</div>
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-muted/30 p-5">
                  <h3 className="mb-3 text-sm font-semibold">Cost Breakdown</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Current monthly cost</span>
                      <span className="font-medium text-foreground">
                        ${currentTotalCost.toLocaleString()}/mo
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Peakflow cost</span>
                      <span className="font-medium text-green-500">${peakflowCost}/mo</span>
                    </div>
                    <div className="border-t border-border pt-2">
                      <div className="flex justify-between">
                        <span className="font-medium text-foreground">Monthly savings</span>
                        <span className="font-bold text-green-500">
                          ${monthlySavings.toLocaleString()}/mo
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <Button size="lg" className="w-full" asChild>
                  <Link href="/demo">
                    Get These Results <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  Based on average Peakflow customer data. Individual results may vary.
                </p>
              </motion.div>
            ) : (
              <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-border p-10 text-center">
                <div>
                  <p className="text-muted-foreground">
                    Fill in your current setup and click &ldquo;Calculate My ROI&rdquo; to see your projected results.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
